import { useQuery } from '@tanstack/react-query';
import { demoAICalibrationEnabledEmployees } from 'Deer/metricCards/constants';
import { sendRequest } from 'api/sendRequest';
import { useQuery as useQueryParams } from 'components/hooks/useQuery';
import { useReviewManageSidepanelData } from 'reducers/reviewManageSidepanelSlice';
import { useSelectedView } from 'reducers/viewsSlice';
import {
  checkDemoAICalibrationEnabled,
  checkStaticDataCalibrationEnabled,
  checkEnableCompanySupport,
} from 'utils/helpers/featureFlags';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';
import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { getFilters, getReviewCyclesAndCalibrationViews } from 'services/analytics/review';

import { useGetReviewManageSidepanelInfo } from './reviewManageSidepanel/dataHooks';

// import { useReviewManagePanelQueryParams } from './reviewManageSidepanel/sidepanelStateHooks';

const getReviewsManageGridDataApi = ({ cohortId, reviewCycleId }) => {
  const url = endpoints.performanceReview.manage.reviewMangeGridData;
  return sendRequest({
    url,
    method: 'GET',
    credentials: 'include',
    queryParams: { cohortId, reviewCycleId },
  });
};

export const setReviewManageRowDefaultValues = (row) => {
  const newRow = { ...row };

  if (!newRow.COMPANY) {
    newRow.COMPANY = { id: 'NA', companyName: 'NA' };
  }
  if (!newRow.LOCATION) {
    newRow.LOCATION = 'NA';
  }
  return newRow;
};

export const useGetReviewsManageTabularDataQueryV1 = () => {
  const cohort = useSelectedView();
  // const rawReviewCycleId = Number(useQueryParams().reviewCycleId);
  const rawReviewCycleId = useQueryParams().reviewCycleId;

  // const reviewCycleId = Number.isNaN(rawReviewCycleId) ? null : rawReviewCycleId;
  const reviewCycleId = rawReviewCycleId ?? null;

  return useQuery(
    [REACT_QUERY_KEYS.reviewsManage, cohort?.id, reviewCycleId],
    () =>
      getReviewsManageGridDataApi({ cohortId: cohort?.id, reviewCycleId }).then((res) => {
        // manually adding "Yes" to the column value 'IDENTIFIED_FOR_CALIBRATION' for demo
        // specifically modifying 6 entries with IDs in the array - demoAICalibrationEnabledEmployees
        const { entity } = res;

        if (checkEnableCompanySupport()) {
          entity.rows = entity.rows.map(setReviewManageRowDefaultValues);
        }
        if (checkStaticDataCalibrationEnabled() && checkDemoAICalibrationEnabled()) {
          const DEFAULT_VALUE = 'No';
          const COLUMN_ID = 'IDENTIFIED_FOR_CALIBRATION';
          entity.rows = entity.rows.map((value) => {
            const employeeId = value?.EMPLOYEE?.id.toString();
            let isIdentifiedForCalibration = DEFAULT_VALUE;
            if (demoAICalibrationEnabledEmployees.includes(employeeId)) {
              isIdentifiedForCalibration = 'Yes';
            }

            return {
              ...value,
              [COLUMN_ID]: isIdentifiedForCalibration,
            };
          });
          return entity;
        }

        return entity;
      }),
    {
      enabled: cohort?.id !== undefined && !!reviewCycleId,
    }
  );
};

export const useGetAnalyticsFiltersDataQuery = () => useQuery([REACT_QUERY_KEYS.reviewsManageFilters], getFilters);

export const useGetReviewCyclesAndCalibrationViews = ({ cohortId, isPlanOfAction }) =>
  useQuery(
    [REACT_QUERY_KEYS.reviews.reviewCyclesAndViews, cohortId, isPlanOfAction],
    () => getReviewCyclesAndCalibrationViews({ cohortId, isPlanOfAction }),
    {
      enabled: isPlanOfAction || !!cohortId,
    }
  );

const getReviewHistory = ({ empId }) => {
  const url = endpoints.performanceReview.manage.reviewHistory;
  return sendRequest({
    url,
    method: 'GET',
    credentials: 'include',
    queryParams: { empId },
  });
};

export const useGetReviewManageReviewHistory = () => {
  const { empId } = useReviewManageSidepanelData();

  return useQuery(
    [REACT_QUERY_KEYS.reviewsManageReviewHistoryGraph, empId],
    () => getReviewHistory({ empId }).then((res) => res.entity),
    {
      enabled: !!empId,
    }
  );
};

const getReviewManageProvidersData = ({ empId, reviewCycleId }) => {
  const url = endpoints.performanceReview.manage.providersData;
  return sendRequest({
    url,
    method: 'GET',
    credentials: 'include',
    queryParams: { employeeId: empId, reviewCycleId },
  });
};

export const useGetReviewManageProvidersData = () => {
  const { selectedReviewCycle, empId } = useReviewManageSidepanelData();

  const reviewCycleId = selectedReviewCycle?.uuid;

  // const { reviewManageEmpId: empId, reviewManageReviewId: reviewCycleId } = useReviewManagePanelQueryParams();

  return useQuery(
    [REACT_QUERY_KEYS.reviewManage.providers, empId],
    () => getReviewManageProvidersData({ empId, reviewCycleId }).then((res) => res.entity),
    {
      enabled: !!empId && !!reviewCycleId,
      staleTime: 60000,
    }
  );
};

export const useSidepanelManagerRatingData = () => {
  const { data, isLoading } = useGetReviewManageProvidersData();

  if (!data) return { managerData: data, isLoading };
  const managerData = data.find(({ response }) => response.providerType === REVIEW_PROVIDER_TYPES.MANAGER);
  return { managerData, isLoading };
};

export const useSidepanelSelfRatingData = () => {
  const { data, isLoading } = useGetReviewManageProvidersData();

  if (!data) return { isLoading, selfData: data };
  const selfData = data.find(({ response }) => response.providerType === REVIEW_PROVIDER_TYPES.SELF);
  return { isLoading, selfData };
};

export const useSidepanelSelfData = () => {
  const { data, isLoading } = useGetReviewManageSidepanelInfo();

  if (!data) return { selfData: data, isLoading };

  const targetCycleId = data?.metadata?.targetCycleId;
  const feedbackFormId = data?.metadata?.reviewCycleIdToFormIdMap?.[targetCycleId];

  const selfData = {
    employee: data?.metadata?.employee,
    response: {
      feedbackFormId,
    },
  };

  return {
    selfData,
    isLoading,
  };
};
export const useSidepanelPeerRatingData = () => {
  const { data, isLoading } = useGetReviewManageProvidersData();

  if (!data) return { peerData: [], isLoading };
  const peerData = data.filter(({ response }) => response.providerType === REVIEW_PROVIDER_TYPES.PEER);
  return { peerData, isLoading };
};

export const useSidepanelUpwardRatingData = () => {
  const { data, isLoading } = useGetReviewManageProvidersData();

  if (!data) return { upwardData: [], isLoading };
  const upwardData = data.filter(({ response }) => response.providerType === REVIEW_PROVIDER_TYPES.UPWARD);
  return { upwardData, isLoading };
};

export const useSidepanelDLMRatingData = () => {
  const { data, isLoading } = useGetReviewManageProvidersData();

  if (!data) return { dlmData: [], isLoading };
  const dlmData = data.filter(({ response }) => response.providerType === REVIEW_PROVIDER_TYPES.DLM);
  return { dlmData, isLoading };
};
